import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { Layout } from '../components/shared/Layout';
import { Alert } from '../components/shared/Alert';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { supportApi } from '../services/api';
import { useAuthStore } from '../stores/authStore';

const supportSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  subject: z.string().min(3, 'Subject must be at least 3 characters'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

type SupportFormData = z.infer<typeof supportSchema>;

export function SupportPage() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuthStore();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SupportFormData>({
    resolver: zodResolver(supportSchema),
    defaultValues: {
      name: user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '',
      email: user?.email ?? '',
    },
  });

  const onSubmit = async (data: SupportFormData) => {
    setError(null);
    setIsLoading(true);
    try {
      await supportApi.submitContact(data);
      setSuccess(true);
      reset();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-12">
        <div className="text-center mb-8">
          <ChatBubbleLeftRightIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">Contact Support</h1>
          <p className="mt-2 text-gray-600">
            Have a question or need help? Fill out the form below and our team will get back to you.
          </p>
        </div>

        <div className="card mb-8">
          <div className="flex items-center space-x-3">
            <EnvelopeIcon className="h-5 w-5 text-primary-600" />
            <span className="text-gray-700">support@amisecretlymarried.co.za</span>
          </div>
        </div>

        {success ? (
          <div className="card">
            <Alert
              type="success"
              title="Message Sent"
              message="Thank you for contacting us. We will respond to your inquiry as soon as possible."
            />
          </div>
        ) : (
          <form className="card space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && <Alert type="error" message={error} />}

            <div>
              <label htmlFor="name" className="label">
                Your Name
              </label>
              <input
                id="name"
                type="text"
                className={`input ${errors.name ? 'input-error' : ''}`}
                placeholder="John Doe"
                {...register('name')}
              />
              {errors.name && <p className="error-text">{errors.name.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className={`input ${errors.email ? 'input-error' : ''}`}
                placeholder="john@example.com"
                {...register('email')}
              />
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="subject" className="label">
                Subject
              </label>
              <input
                id="subject"
                type="text"
                className={`input ${errors.subject ? 'input-error' : ''}`}
                placeholder="How can we help?"
                {...register('subject')}
              />
              {errors.subject && <p className="error-text">{errors.subject.message}</p>}
            </div>

            <div>
              <label htmlFor="message" className="label">
                Message
              </label>
              <textarea
                id="message"
                rows={5}
                className={`input ${errors.message ? 'input-error' : ''}`}
                placeholder="Please describe your question or issue..."
                {...register('message')}
              />
              {errors.message && <p className="error-text">{errors.message.message}</p>}
            </div>

            <button type="submit" className="btn-primary w-full py-3" disabled={isLoading}>
              {isLoading ? <LoadingSpinner size="sm" /> : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </Layout>
  );
}
